import { apiBackend, getCurrentUserEmail } from './trainingService';
import { TrainingMaster, Training } from '../types/training';
import {
    getMoodlePrograms,
    getMoodleProgramDetails,
    updateMoodleProgram,
    deleteMoodleProgram,
    toggleMoodleProgramStatus,
    enrollMoodleUser,
    getEnrollmentRequests,
    approveEnrollmentRequest,
    rejectEnrollmentRequest,
    sendProgramUpdateNotification
} from './moodleService';


// One /api/me call per signed-in user, however many pages ask
const adminChecks = new Map<string, Promise<boolean>>();

/**
 * Check if the current user has admin privileges. The admin list lives in
 * the API (api/shared/roles.ts); it answers from the sign-in token.
 */
export const checkIsAdmin = async (): Promise<boolean> => {
    try {
        const userEmail = (await getCurrentUserEmail()).toLowerCase();
        if (!adminChecks.has(userEmail)) {
            adminChecks.set(userEmail, apiBackend.get('/me').then(r => !!r.data?.isAdmin));
        }
        return await adminChecks.get(userEmail)!;
    } catch (error: any) {
        console.warn('⚠️ Admin check failed:', error);
        adminChecks.clear();
        return false;
    }
};

/**
 * Check if the current user is listed as a stakeholder on at least one program.
 * Reuses the same getMoodlePrograms call the participant dashboard already makes -
 * each program already carries isStakeholder for the given email.
 */
export const checkIsStakeholder = async (): Promise<boolean> => {
    try {
        const userEmail = await getCurrentUserEmail();
        if (!userEmail) return false;
        const programs: Training[] = await getMoodlePrograms(userEmail);
        return programs.some((p: Training) => p.isStakeholder);
    } catch (error: any) {
        console.warn('⚠️ Stakeholder check failed:', error);
        return false;
    }
};

/**
 * Get all trainings for admin management
 */
export const adminGetAllTrainings = async (): Promise<TrainingMaster[]> => {
    return await getMoodlePrograms();
};

/**
 * Get single training by ID
 */
export const adminGetTrainingById = async (id: number): Promise<TrainingMaster> => {
    try {
        const data = await getMoodleProgramDetails('', id);
        return data as unknown as TrainingMaster;
    } catch (error) {
        console.error('❌ Error fetching training from Moodle:', error);
        throw error;
    }
};

export const adminGetTrainingByTrainingID = async (trainingId: string): Promise<TrainingMaster | null> => {
    try {
        const data = await getMoodleProgramDetails(trainingId, 0);
        return data as unknown as TrainingMaster;
    } catch (error) {
        console.warn(`⚠️ Training ${trainingId} not found in Moodle`);
        return null;
    }
};

/**
 * Create a new training program
 */
export const adminCreateTraining = async (trainingData: any): Promise<void> => {
    // Moodle creation is handled directly via createMoodleProgram in CreateProgram workflow
    console.log('📝 Initializing Moodle Program Creation');
};

/**
 * Update an existing training program
 */
export const adminUpdateTraining = async (id: number, data: any): Promise<void> => {
    try {
        await updateMoodleProgram({
            id,
            ...data
        });
    } catch (error) {
        console.error('Error in adminUpdateTraining:', error);
        throw error;
    }
};

/**
 * Bulk assign participants to a training
 */
export const adminAssignParticipants = async (
    trainingId: string,
    moduleIds: string[],
    participantEmails: string[],
    role: string = 'student'
): Promise<void> => {
    try {
        const training = await adminGetTrainingByTrainingID(trainingId);
        if (!training) throw new Error("Training not found in Moodle");

        for (const email of participantEmails) {
            await enrollMoodleUser(training.id, email, role);
        }
        console.log(`✅ Users enrolled in Moodle as ${role} successfully`);
    } catch (error) {
        console.error(`❌ Error enrolling as ${role}:`, error);
        throw error;
    }
};

export const adminToggleTrainingStatus = async (id: number, currentStatus: any): Promise<void> => {
    try {
        const newStatus = !currentStatus;
        await toggleMoodleProgramStatus(id, newStatus);
        console.log(`✅ Training ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
        console.error('❌ Error toggling training status:', error);
        throw error;
    }
};

export const adminDeleteTraining = async (dbId: number, trainingId: string): Promise<void> => {
    try {
        console.log(`🚀 Deleting Training from Moodle: ${trainingId} (ID: ${dbId})`);
        await deleteMoodleProgram(dbId);
        console.log('✅ Training deleted successfully from Moodle');
    } catch (error: any) {
        console.error('❌ Error deleting training:', error);
        throw error;
    }
};

/**
 * Dummy stubs for legacy functions to prevent UI breakage
 */
export const adminBulkUploadQuestions = async (...args: any[]) => console.log('⚠️ Legacy function bypassed: adminBulkUploadQuestions', args);
export const adminSaveSubjectiveQuestions = async (...args: any[]) => console.log('⚠️ Legacy function bypassed: adminSaveSubjectiveQuestions', args);
export const adminClearSubmissionsByTraining = async (...args: any[]) => console.log('⚠️ Legacy function bypassed: adminClearSubmissionsByTraining', args);
export const adminFetchObjectiveQuestions = async () => [];
export const adminFetchSubjectiveQuestions = async () => [];
export const adminGetEnrollmentRequests = async (): Promise<any[]> => {
    try {
        const requests = await getEnrollmentRequests();
        return requests.map((r: any) => ({
            Id: r.Id || r.id,
            ParticipantEmail: r.ParticipantEmail || r.participantemail,
            TrainingID: r.TrainingID || r.trainingid,
            status: r.status || 'Requested',
            AssignedDate: r.AssignedDate || (r.assigneddate ? new Date(r.assigneddate * 1000).toISOString() : new Date().toISOString())
        }));
    } catch (error) {
        console.error('❌ Error fetching enrollment requests:', error);
        return [];
    }
};

export const adminApproveEnrollment = async (requestId: number): Promise<void> => {
    try {
        await approveEnrollmentRequest(requestId);
    } catch (error) {
        console.error('❌ Error approving enrollment:', error);
        throw error;
    }
};

export const adminRejectEnrollment = async (requestId: number): Promise<void> => {
    try {
        await rejectEnrollmentRequest(requestId);
    } catch (error) {
        console.error('❌ Error rejecting enrollment:', error);
        throw error;
    }
};

/**
 * Executive Dashboard
 */
export const adminGetExecutiveDashboardData = async (): Promise<any> => {
    try {
        const response = await apiBackend.get('/executive-dashboard-data');
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching executive dashboard data:', error);
        throw error;
    }
};

export const adminGenerateChapterModules = async (programName: string, subjectName: string, chapterName: string, extraPrompt?: string): Promise<{ name: string; description: string }[]> => {
    try {
        const topic = `Program: "${programName}". Subject: "${subjectName}". Chapter: "${chapterName}". Generate focused learning modules for this chapter.`;
        const response = await apiBackend.post('/generateCurriculum', { topic, extraPrompt });
        return response.data.curriculum?.modules || [];
    } catch (error: any) {
        console.error('❌ Error generating chapter modules:', error);
        throw error;
    }
};

export const adminGenerateAIQuestions = async (topic: string, difficulty: string, count: number, extraPrompt?: string): Promise<any> => {
    try {
        const response = await apiBackend.post('/generateQuestions', { topic, difficulty, count, extraPrompt });
        return {
            questions: response.data.questions || (Array.isArray(response.data) ? response.data : []),
            estimatedTimeMinutes: response.data.estimatedTimeMinutes || (count * 5)
        };
    } catch (error: any) {
        console.error('❌ Error generating AI questions:', error);
        throw error;
    }
};

export const adminSummarizeChanges = async (changes: { field: string; value: string }[]): Promise<string> => {
    try {
        const response = await apiBackend.post('/summarizeChanges', { changes });
        return response.data.payload || '';
    } catch (error: any) {
        console.error('❌ Error summarizing changes:', error);
        return '';
    }
};

// One AI result per assignment question - see specs/2026-10-16-ai-eval-question-focus.md.
// "verdict" is about the answer's quality; "cannot_assess" is the only
// uncertainty signal (the AI genuinely couldn't judge that question).
// "flags" are facilitator follow-ups on that answer (they replaced the old
// evaluation criteria).
export interface QuestionFlag {
    type: 'unclear_concept' | 'disagrees' | 'open_question' | 'challenge_thinking';
    note: string;
}

export interface QuestionResult {
    no: string;
    title: string;
    verdict: 'met' | 'partial' | 'not_met' | 'missing' | 'cannot_assess';
    reason: string;
    answer_excerpt?: string;
    flags?: QuestionFlag[];
}

export const draftEvaluationWithAI = async (params: {
    assignmentTitle: string;
    instructions: string;
    answerText: string;
    maxCredits: number;
    // The trainer's checklist - the AI takes it into account, it is not scored.
    criteria?: { name: string }[];
}): Promise<{
    evaluation: string;
    total_score: number;
    overallConfidence?: 'high' | 'medium' | 'low';
    question_results?: QuestionResult[];
    possibly_ai_written?: { reason: string };
} | null> => {
    try {
        const response = await apiBackend.post('/evaluateSubmission', params);
        return {
            evaluation: response.data.evaluation || '',
            total_score: response.data.total_score ?? 0,
            overallConfidence: response.data.overallConfidence,
            question_results: response.data.question_results || [],
            ...(response.data.possibly_ai_written ? { possibly_ai_written: response.data.possibly_ai_written } : {}),
        };
    } catch (error: any) {
        console.error('❌ Error drafting AI evaluation:', error);
        return null;
    }
};

export const extractAssignmentQuestions = async (params: {
    assignmentTitle: string;
    referenceText: string;
}): Promise<{ found: boolean; questions: string } | null> => {
    try {
        const response = await apiBackend.post('/extractAssignmentQuestions', params);
        return {
            found: !!response.data.found,
            questions: response.data.questions || '',
        };
    } catch (error: any) {
        console.error('❌ Error extracting assignment questions:', error);
        return null;
    }
};

export const adminSendProgramUpdateNotification = async (courseId: number, changeSummary: string, modifiedBy?: string): Promise<void> => {
    try {
        await sendProgramUpdateNotification(courseId, changeSummary, modifiedBy);
    } catch (error: any) {
        console.error('❌ Error sending update notification:', error);
    }
};
